import { z } from 'zod';
import { concat, isEmpty, isNil, map } from 'lodash';
import { LeftNavSection, ConfDefnTab, AssortmentConfDefn, DisabledTab } from './confdefn';
import { SectionView } from './confdefnComponents';
export function tenantTabHasPathSlot(tab) {
    return tab.pathSlot !== undefined;
}
export const viewRequiresMfpScope = (view) => {
    const viewType = view.boundViewComponentType;
    // NOTE: New mfp components must be added to this array if they need to detect their disabled state based on
    // the `mfpModule` context object property `onlyIndirectMfpScoping`
    return ['MfpSummaryGrid', 'MfpFavorite', 'MfpSplitView', 'MfpReviewPlans', 'MfpReviewPrivatePlans'].includes(viewType);
};
function filterInvalidItem(item) {
    return !isNil(item);
}
function formatAndLogLocal(errorMessage, errors) {
    console.groupCollapsed(`%c${errorMessage}`, 'font-size: 14px');
    console.log(`%c${'*** Basic details below. For more detailed errors enable debug/verbose messages in browser console ***'}`, 'padding: 8px 0; color: #4298db');
    errors.forEach((error) => console.log(`%c${error}`, 'padding: 8px 0'));
    console.groupEnd();
}
function validateLevel(schema, input, throwOnInvalid) {
    let level;
    try {
        level = schema.parse(input);
        return level;
    }
    catch (error) {
        if (error instanceof z.ZodError) {
            console.debug(error.message);
            if (throwOnInvalid) {
                throw error;
            }
            return level;
        }
        else {
            throw error;
        }
    }
}
export function validateAtLevel(schema, input, options = {}) {
    const { childLevel = '', throwOnInvalid = false } = options;
    // The following schema/types provide an "allowable" level type (confdefn, tab, section, view) that
    // omit the "children" of that level that may be misconfigured causing the whole level to be invalid.
    // So omit the "children" at that level so the level can pass validation independent of its "children".
    // Need to convert to slightly different zod type because 'omit' doesn't exist on union types (think view level)
    const AllowableSchema = isEmpty(childLevel) ? schema : schema.omit({ [childLevel]: true });
    return validateLevel(AllowableSchema, input, throwOnInvalid);
}
/**
 * Validate each level of the confdefn tree, filtering out invalids items.
 * An invalid item at a higher level removes that level and its children.
 *
 * Current levels:
 * - UIConfDefn level (minus the tabs)
 * - Tabs level (minus the sections)
 * - Sections level (minus the views)
 * - Views level (all views)
 *
 * @param {UIConfDefn} input - the unvalidated confdefn
 *
 * @returns {TenantConfig} a validated/filtered confdefn
 */
export function validateConfDefn(input, loggingService, alwaysThrowOnInvalid = false) {
    let errors = [];
    // Require valid top level letting error propagate to catch if invalid
    const uiConfDefnLevel = validateAtLevel(AssortmentConfDefn, input, {
        childLevel: 'tabs',
        throwOnInvalid: alwaysThrowOnInvalid || true,
    });
    // Validate at each level (tab, section, view)
    const childLevels = map(input.tabs, (tab, tabIndex) => {
        // validate disabled tab
        if (tab.disabled) {
            const validatedDisabledTab = validateAtLevel(DisabledTab, tab, { throwOnInvalid: alwaysThrowOnInvalid });
            if (isNil(validatedDisabledTab)) {
                const error = `Invalid tab item [${tab.id}] detected at position [${tabIndex}] in 'tabs' array.`;
                errors = concat(errors, error);
                return;
            }
            return validatedDisabledTab;
        }
        // validate enabled tab and reinsert tab children after validation
        const validatedTabLevel = validateAtLevel(ConfDefnTab, tab, {
            childLevel: 'leftNavSections',
            throwOnInvalid: alwaysThrowOnInvalid,
        });
        if (isNil(validatedTabLevel)) {
            const error = `Invalid tab item [${tab.id}] detected at position [${tabIndex}] in 'tabs' array.`;
            errors = concat(errors, error);
            return;
        }
        const validatedTabLevelWithChildren = Object.assign(Object.assign({}, validatedTabLevel), { leftNavSections: tab.leftNavSections });
        // validate all sections in tab
        const validatedSectionLevels = map(validatedTabLevelWithChildren.leftNavSections, (section, sectionIndex) => {
            // validate section and reinsert section children after validation
            const validatedSectionLevel = validateAtLevel(LeftNavSection, section, {
                childLevel: 'views',
                throwOnInvalid: alwaysThrowOnInvalid,
            });
            if (isNil(validatedSectionLevel)) {
                const error = `Invalid section item [${section.id}] detected in 'leftNavSections' array at tab position [${tabIndex}] -> leftNavSections position [${sectionIndex}].`;
                errors = concat(errors, error);
                return;
            }
            const validatedSectionLevelWithChildren = Object.assign(Object.assign({}, validatedSectionLevel), { views: section.views });
            // validate all views in section
            const validatedViewLevels = map(validatedSectionLevelWithChildren.views, (view, viewIndex) => {
                const validatedViewLevel = validateAtLevel(SectionView, view, { throwOnInvalid: alwaysThrowOnInvalid });
                if (isNil(validatedViewLevel)) {
                    const error = `Invalid view item [${view.id}] detected in 'views' array at tabs position [${tabIndex}] -> leftNavSections position [${sectionIndex}] -> views position [${viewIndex}].`;
                    errors = concat(errors, error);
                    view.errorView = true;
                    return view;
                }
                return validatedViewLevel;
            }).filter(filterInvalidItem);
            return Object.assign(Object.assign({}, validatedSectionLevel), { views: validatedViewLevels });
        }).filter(filterInvalidItem); // filter out invalid (undefined) section(s)
        return Object.assign(Object.assign({}, validatedTabLevel), { leftNavSections: validatedSectionLevels });
    }).filter((tab) => !isNil(tab)); // filter out invalid (undefined) tab(s)
    if (!isEmpty(errors)) {
        const errorMessage = 'INVALID UI CONFDEFN ITEMS DETECTED:';
        // true skips the logging to browser console
        loggingService.warn(errorMessage, errors.join('\n'), true);
        formatAndLogLocal(errorMessage, errors);
    }
    // validated/filtered result
    return Object.assign(Object.assign({}, uiConfDefnLevel), { tabs: childLevels });
}
